import {
  BarChart3,
  CalendarDays,
  FileUp,
  Handshake,
  Home,
  Inbox,
  Kanban,
  MoreHorizontal,
  PhoneCall,
  Plus,
  Settings,
  TrendingUp,
  User,
  UsersRound,
  Wallet,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";
import { Link, matchPath, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { useEsAdmin } from "../datos/useEsAdmin";
import { useMiRol } from "../datos/useMiRol";
import { PipelinePage } from "../pipeline/PipelinePage";

type Icono = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type Entrada = { clave: string; ruta: string; patron: string; Icono: Icono };

/**
 * Barra inferior de navegación en móvil (copia adaptada de layout/MobileNavigation.tsx
 * de Atomic): Inicio, Leads, «+» (nueva ficha), Pipeline y Agenda, y «Más» con el resto de
 * pantallas de cada papel (auditoría 30/10/2026: desde el móvil no se llegaba a Equipo,
 * Comisiones ni Ajustes). El consultor no ve Leads ni el «+»: no recibe leads.
 */
const INICIO: Entrada = {
  clave: "nevora.nav.inicio",
  ruta: "/",
  patron: "/",
  Icono: Home,
};
const LEADS: Entrada = {
  clave: "nevora.nav.leads",
  ruta: "/negocios",
  patron: "/negocios/*",
  Icono: PhoneCall,
};
const DERECHA: Entrada[] = [
  {
    clave: "nevora.nav.pipeline",
    ruta: PipelinePage.path,
    patron: `${PipelinePage.path}/*`,
    Icono: Kanban,
  },
  {
    clave: "nevora.nav.agenda",
    ruta: "/agenda",
    patron: "/agenda/*",
    Icono: CalendarDays,
  },
];

/** Lo que va en «Más», según el papel. */
const MAS_ADMIN: Entrada[] = [
  {
    clave: "nevora.nav.sin_asignar",
    ruta: "/sin-asignar",
    patron: "/sin-asignar/*",
    Icono: Inbox,
  },
  {
    clave: "nevora.nav.colaboraciones",
    ruta: "/colaboraciones",
    patron: "/colaboraciones/*",
    Icono: Handshake,
  },
  {
    clave: "nevora.nav.equipo",
    ruta: "/equipo",
    patron: "/equipo/*",
    Icono: UsersRound,
  },
  {
    clave: "nevora.nav.comisiones",
    ruta: "/comisiones",
    patron: "/comisiones/*",
    Icono: Wallet,
  },
  {
    clave: "nevora.nav.resumen",
    ruta: "/resumen",
    patron: "/resumen/*",
    Icono: BarChart3,
  },
  {
    clave: "nevora.nav.informe",
    ruta: "/informe",
    patron: "/informe/*",
    Icono: TrendingUp,
  },
  {
    clave: "nevora.nav.importar",
    ruta: "/importar",
    patron: "/importar/*",
    Icono: FileUp,
  },
  {
    clave: "nevora.nav.ajustes",
    ruta: "/ajustes",
    patron: "/ajustes/*",
    Icono: Settings,
  },
];
const MAS_SETTER: Entrada[] = [
  {
    clave: "nevora.nav.resumen",
    ruta: "/resumen",
    patron: "/resumen/*",
    Icono: BarChart3,
  },
  {
    clave: "nevora.nav.informe",
    ruta: "/informe",
    patron: "/informe/*",
    Icono: TrendingUp,
  },
  {
    clave: "nevora.nav.mis_comisiones",
    ruta: "/mis-comisiones",
    patron: "/mis-comisiones/*",
    Icono: Wallet,
  },
];
const MAS_CONSULTOR: Entrada[] = [
  {
    clave: "nevora.nav.mis_comisiones",
    ruta: "/mis-comisiones",
    patron: "/mis-comisiones/*",
    Icono: Wallet,
  },
];
const PERFIL: Entrada = {
  clave: "nevora.nav.perfil",
  ruta: "/perfil",
  patron: "/perfil/*",
  Icono: User,
};

export const NavegacionMovil = () => {
  const translate = useTranslate();
  const location = useLocation();
  const esAdmin = useEsAdmin();
  const { esConsultor } = useMiRol();
  const [mas, setMas] = useState(false);
  const esPwa = window.matchMedia("(display-mode: standalone)").matches;
  const esIos = /iPad|iPod|iPhone/.test(window.navigator.userAgent);
  const activo = (patron: string) =>
    patron === "/"
      ? location.pathname === "/"
      : !!matchPath(patron, location.pathname);
  const enMas = (
    esAdmin ? MAS_ADMIN : esConsultor ? MAS_CONSULTOR : MAS_SETTER
  ).concat(PERFIL);

  const boton = ({ clave, ruta, patron, Icono }: Entrada) => (
    <Button
      key={ruta}
      asChild
      variant="ghost"
      className={cn(
        "flex-col gap-1 h-auto py-2 px-1 rounded-md w-14",
        activo(patron) ? "text-foreground" : "text-muted-foreground",
      )}
    >
      <Link to={ruta} data-guia={`nav-${clave.split(".").pop()}`}>
        <Icono className="size-6" />
        <span className="text-[10px] font-medium">{translate(clave)}</span>
      </Link>
    </Button>
  );

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 h-14 border-t border-border bg-secondary/95 backdrop-blur"
      style={{
        // iOS no descuenta la zona segura inferior en modo PWA: se añade a mano.
        paddingBottom: esPwa && esIos ? 15 : undefined,
        height: esPwa && esIos ? "calc(3.5rem + 15px)" : undefined,
      }}
    >
      <div className="flex justify-center items-start">
        {boton(INICIO)}
        {!esConsultor ? boton(LEADS) : null}
        {!esConsultor ? (
          <Button
            asChild
            size="icon"
            className="h-14 w-14 rounded-full -mt-4 mx-1 shadow-elevada"
            aria-label={translate("nevora.negocios.nueva_ficha")}
          >
            <Link to="/negocios/create">
              <Plus className="size-7" />
            </Link>
          </Button>
        ) : null}
        {DERECHA.map(boton)}
        <Button
          variant="ghost"
          className={cn(
            "flex-col gap-1 h-auto py-2 px-1 rounded-md w-14",
            enMas.some((e) => activo(e.patron))
              ? "text-foreground"
              : "text-muted-foreground",
          )}
          onClick={() => setMas(true)}
          aria-label={translate("nevora.nav.mas")}
          data-guia="nav-mas"
        >
          <MoreHorizontal className="size-6" />
          <span className="text-[10px] font-medium">
            {translate("nevora.nav.mas")}
          </span>
        </Button>
      </div>
      <Sheet open={mas} onOpenChange={setMas}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader>
            <SheetTitle>{translate("nevora.nav.mas")}</SheetTitle>
          </SheetHeader>
          <ul className="grid grid-cols-3 gap-2 px-4">
            {enMas.map((e) => (
              <li key={e.ruta}>
                <Link
                  to={e.ruta}
                  onClick={() => setMas(false)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-center text-xs font-medium",
                    activo(e.patron)
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  <e.Icono className="size-6" />
                  {translate(e.clave)}
                </Link>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
    </nav>
  );
};
